/** @jsxImportSource @emotion/react */

import { useEffect, useRef } from 'react';

import useStatefulInput, {
  IInputValidator,
} from '../../../../../hooks/useStatefulInput';
import useAction, { IAction } from '../../../../../hooks/useAction';

import RadioButton from '../../../../../components/radioButton';
import AttributedText from '../../../../../components/attributedText';
import { IBlockBaseItem } from '../blockItem';

export interface IBlockRadioButtonItem extends IBlockBaseItem {
  type: 'radio_button';

  inputId: string;
  selected: false;
  itemId: string;
  validator?: IInputValidator;
  inputAccessibility?: { legend: string; legendIdentifier: string };

  action: IAction;
  // Internally used not from view-layer this is to group radio button and related text, to group together as radio group
  textLegend?: string;
}

interface IBlockRadioButtonItemProps {
  data: IBlockRadioButtonItem;
}

const BlockRadioButtonItem = ({
  data,
  ...props
}: IBlockRadioButtonItemProps) => {
  const {
    inputValue,
    isValid,
    isDirty,
    isTouched,
    setInputValue,
    setIsTouched,
  } = useStatefulInput<string | null>({
    inputId: data.inputId,
    initialValue: data.selected ? data.itemId : null,
    validators: data.validator ? [data.validator] : undefined,

    keepLastOnNull: true,
  });

  const handleAction = useAction();
  const hasMountedRef = useRef(false);
  const actionRef = useRef(data.action);

  const shouldShowErrorState = !isValid && isDirty && isTouched;

  useEffect(() => {
    if (hasMountedRef.current) {
      handleAction(actionRef.current);
    }
    hasMountedRef.current = true;
  }, [handleAction, inputValue]);

  return (
    <div
      role='radiogroup'
      {...props}
      css={{
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
      }}
    >
      <RadioButton
        name={data.inputId}
        checked={inputValue === data.itemId}
        onChange={(e) => {
          e.stopPropagation();
          setInputValue(data.itemId);
        }}
        onBlur={() => {
          setIsTouched();
        }}
        isInvalid={shouldShowErrorState}
        aria-invalid={shouldShowErrorState}
        aria-errormessage={`${data.inputId}-validation-error`}
      />
      {data.textLegend && (
        <span css={{ marginLeft: 8 }}>
          <AttributedText text={data.textLegend} id={data.accessibility?.id} />
        </span>
      )}
    </div>
  );
};

export default BlockRadioButtonItem;
